import React from "react";
import { Button, Card, CardHeader, Col, Row, Spinner } from "reactstrap";

import {
  autopilotCardStyle,
  autopilotHeroDescriptionStyle,
  autopilotHeroEyebrowStyle,
  autopilotHeroHeaderStyle,
  autopilotHeroTitleStyle,
  autopilotSecondaryButtonStyle,
} from "../autopilot/styles";

/**
 * Hero PADRÃO de página do Hub (PO1-2724). É o mesmo cabeçalho das telas de
 * Auditoria de CT-e / Auditoria de Faturas: Card `card-everlog-shell` com fundo
 * `--everlog-card-hero-bg`, eyebrow em cor de destaque, título `<h2>`,
 * descrição e ações no topo direito. Tudo por token: acompanha tema claro/escuro
 * e a cor de destaque escolhida em Preferências.
 *
 * Props:
 * - eyebrow, title, description: textos do cabeçalho (title sempre vira <h2>).
 * - actions: nó com os botões do topo direito (use PageHeroRefreshButton /
 *   PageHeroExportButton para "↺ Atualizar" e "Exportar CSV").
 * - aside: nó opcional à direita (ex.: painel "Contexto atual"). Com `actions`
 *   e `aside` juntos, os dois empilham alinhados à direita.
 * - children: conteúdo extra abaixo da descrição (pills de contexto etc.).
 * - className, style: mesclados no Card.
 */
const actionsStyle = {
  display: "flex",
  gap: "0.5rem",
  flexWrap: "wrap",
  justifyContent: "flex-end",
};

const sideColumnStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: "0.75rem",
};

export default function PageHero({
  eyebrow,
  title,
  description,
  actions,
  aside,
  className = "",
  style,
  children,
}) {
  const hasSide = Boolean(actions || aside);

  return (
    <Card
      className={`card-everlog-shell hub-page-hero ${className}`.trim()}
      style={{ ...autopilotCardStyle, background: "var(--everlog-card-hero-bg)", ...style }}
    >
      <CardHeader style={autopilotHeroHeaderStyle}>
        <Row className="align-items-start">
          <Col>
            {eyebrow ? <div style={autopilotHeroEyebrowStyle}>{eyebrow}</div> : null}
            <h2 style={autopilotHeroTitleStyle}>{title}</h2>
            {description ? <div style={autopilotHeroDescriptionStyle}>{description}</div> : null}
            {children}
          </Col>
          {hasSide ? (
            <Col xs="auto">
              {aside ? (
                <div style={sideColumnStyle}>
                  {actions ? <div style={actionsStyle}>{actions}</div> : null}
                  {aside}
                </div>
              ) : (
                <div style={actionsStyle}>{actions}</div>
              )}
            </Col>
          ) : null}
        </Row>
      </CardHeader>
    </Card>
  );
}

/** Botão "↺ Atualizar" padrão do hero (secundário com gradiente do accent). */
export function PageHeroRefreshButton({
  onClick,
  loading = false,
  disabled = false,
  label = "↺ Atualizar",
  ...rest
}) {
  return (
    <Button
      className="btn-sm"
      style={autopilotSecondaryButtonStyle}
      onClick={onClick}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <Spinner size="sm" /> : label}
    </Button>
  );
}

/** Botão de exportação padrão do hero. Rótulo "Exportar CSV" (export client-side). */
export function PageHeroExportButton({
  onClick,
  loading = false,
  disabled = false,
  label = "Exportar CSV",
  color = "success",
  ...rest
}) {
  return (
    <Button
      color={color}
      className="btn-sm"
      onClick={onClick}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <Spinner size="sm" /> : label}
    </Button>
  );
}
